import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Activity, ArrowRight, Check, Cpu, Gauge, HardDrive, Loader2, MonitorCog, Radio, ShieldCheck, Users, Zap } from "lucide-react";
import { toast } from "sonner";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import Seo from "@/components/Seo";
import DiscordCta from "@/components/DiscordCta";
import api from "@/lib/api";
import { TesterApplyForm } from "@/components/testing/TesterApplyForm";

/* Public landing for the Major Update Testing Program — the "really big deal" page every
   announcement links to. Program copy comes from the backend so renaming the server or
   branch in admin updates every step here. */

const EASE = [0.16, 1, 0.3, 1];
const fade = (d = 0) => ({ initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.6, delay: d, ease: EASE } });
const REQ_ICONS = { os: MonitorCog, ram: Cpu, gpu: Gauge, disk: HardDrive };

export default function TestingPage() {
  const [program, setProgram] = useState(null);
  const [done, setDone] = useState(null);

  useEffect(() => { api.get("/testing/program").then((r) => setProgram(r.data)).catch(() => toast.error("Could not load the program")); }, []);

  if (!program) return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><Loader2 className="w-6 h-6 text-nc-blue animate-spin" /></div>;
  const c = program.counts || {};
  const inCount = (c.approved || 0) + (c.active || 0);

  return (
    <div data-testid="testing-page" className="min-h-screen bg-[#050505] text-zinc-100">
      <Seo title={`${program.name} — Testers Wanted`} description={program.tagline} />
      <SiteNav />

      <section className="relative border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(255,122,24,0.14),transparent_55%),radial-gradient(ellipse_at_bottom_right,rgba(46,107,255,0.12),transparent_55%)]" />
        <div className="relative max-w-6xl mx-auto px-6 py-20 sm:py-28 grid lg:grid-cols-[1.3fr_1fr] gap-12 items-end">
          <div>
            <motion.p {...fade(0)} className="font-mono text-[10px] tracking-[0.4em] text-nc-orange uppercase flex items-center gap-2">
              <span className="w-2 h-2 bg-nc-orange animate-pulse" /> {program.status === "open" ? "Applications open" : program.status === "closed" ? "Round closed" : "Coming soon"} // {program.server_region}
            </motion.p>
            <motion.h1 {...fade(0.05)} className="font-sans font-extrabold uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl text-white mt-4 leading-[1.02]">
              Break the city<br /><span className="text-nc-orange">before it ships.</span>
            </motion.h1>
            <motion.p {...fade(0.1)} className="mt-6 max-w-2xl text-base sm:text-lg text-zinc-400 leading-relaxed">{program.tagline}</motion.p>
            <motion.div {...fade(0.15)} className="mt-8 flex flex-wrap gap-3">
              <a href="#apply" data-testid="testing-hero-apply" className="bg-nc-orange hover:bg-nc-orange/80 text-white font-sans font-semibold uppercase tracking-[0.2em] text-xs px-8 py-4 transition-colors flex items-center gap-2">
                Apply to test <ArrowRight className="w-4 h-4" />
              </a>
              <a href="#how" className="border border-white/20 text-zinc-200 hover:border-white/40 font-sans font-semibold uppercase tracking-[0.2em] text-xs px-8 py-4 transition-colors">How it works</a>
            </motion.div>
          </div>
          <motion.div {...fade(0.2)} className="border border-white/10 bg-black/50 backdrop-blur-md p-6 grid grid-cols-2 gap-6">
            {[["Testers in", inCount, Users], ["On the waitlist", c.waitlisted || 0, Radio], ["Telemetry sessions", c.telemetry_sessions || 0, Activity], ["Test server", program.server_name, Zap]].map(([l, v, I]) => (
              <div key={l} data-testid={`testing-stat-${l.toLowerCase().replace(/\s+/g, "-")}`}>
                <I className="w-4 h-4 text-nc-orange" />
                <p className="font-sans font-extrabold text-2xl text-white mt-2 leading-tight">{v}</p>
                <p className="font-mono text-[9px] tracking-[0.25em] uppercase text-zinc-500 mt-1">{l}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-16">
        <p className="font-mono text-[10px] tracking-[0.4em] text-nc-blue uppercase">Recommended PC</p>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {Object.entries(program.requirements).map(([k, v]) => { const I = REQ_ICONS[k] || Cpu; return (
            <div key={k} className="bg-[#050505] p-6"><I className="w-5 h-5 text-nc-blue" /><p className="font-mono text-[9px] tracking-[0.25em] uppercase text-zinc-500 mt-3">{k}</p><p className="font-mono text-xs text-zinc-200 leading-relaxed mt-1">{v}</p></div>
          ); })}
        </div>
      </section>

      <section id="how" className="border-y border-white/10 bg-[#07080c]">
        <div className="max-w-6xl mx-auto px-6 py-16 grid lg:grid-cols-2 gap-12">
          <div>
            <p className="font-mono text-[10px] tracking-[0.4em] text-nc-orange uppercase">Get in // {program.steps.length} steps</p>
            <ol className="mt-6 space-y-5">
              {program.steps.map((s, i) => (
                <li key={s.title} className="flex gap-4"><span className="font-sans font-extrabold text-2xl text-nc-orange/70 w-8 shrink-0">{i + 1}</span>
                  <div><p className="font-sans font-bold uppercase tracking-widest text-sm text-zinc-100">{s.title}</p><p className="font-mono text-xs text-zinc-500 leading-relaxed mt-1">{s.text}</p></div></li>
              ))}
            </ol>
            <p className="mt-6 font-mono text-[10px] text-zinc-500 leading-relaxed border-l-2 border-nc-orange/40 pl-3">{program.nda_note}</p>
          </div>
          <div>
            <p className="font-mono text-[10px] tracking-[0.4em] text-nc-blue uppercase">Play normally, break things</p>
            <div className="mt-6 grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
              {program.what_to_try.map((w) => (
                <div key={w.title} className="bg-[#07080c] p-5"><p className="font-sans font-bold uppercase tracking-widest text-xs text-zinc-100">{w.title}</p><p className="font-mono text-[11px] text-zinc-500 leading-relaxed mt-2">{w.text}</p></div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-16 grid lg:grid-cols-2 gap-12">
        <div>
          <p className="font-mono text-[10px] tracking-[0.4em] text-nc-orange uppercase">Send us what you find // the most important part</p>
          <div className="mt-6 border border-white/10 divide-y divide-white/10">
            {program.reporting.map((r) => (
              <div key={r.situation} className="p-4 grid sm:grid-cols-[180px_1fr] gap-2"><p className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-400">{r.situation}</p><p className="font-mono text-xs text-zinc-200 leading-relaxed">{r.action}</p></div>
            ))}
          </div>
          <p className="mt-4 font-mono text-[11px] text-zinc-400 bg-black/50 border border-white/10 px-4 py-3">/bug the car door would not open near the pharmacy on Bornwood Blvd</p>
        </div>
        <div>
          <p className="font-mono text-[10px] tracking-[0.4em] text-nc-blue uppercase flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Performance telemetry // opt-in, transparent</p>
          <p className="mt-6 font-mono text-xs text-zinc-400 leading-relaxed">
            With your consent the game sends us performance data while you play: FPS and frame times, game/render/GPU thread timings, ping and packet loss,
            VRAM and RAM use, hitches, where on the map you were and how many players were around. Plus your PC specs once.
            That's how we find the real bottlenecks instead of guessing from reports.
          </p>
          <p className="mt-3 font-mono text-xs text-zinc-500 leading-relaxed">We never read files, keystrokes, chat or anything outside the game's own performance counters. You can pause telemetry any time from your tester portal, and your data is deleted 60 days after the round.</p>
          <div className="mt-6"><DiscordCta testId="testing-discord" compact /></div>
        </div>
      </section>

      <section id="apply" className="border-t border-white/10 bg-[#07080c]">
        <div className="max-w-3xl mx-auto px-6 py-16">
          {done ? (
            <div data-testid="testing-apply-done" className="border border-nc-green/40 bg-nc-green/5 p-8 text-center">
              <Check className="w-8 h-8 text-nc-green mx-auto" />
              <h2 className="font-sans font-extrabold uppercase tracking-tight text-2xl text-white mt-4">{done.status === "approved" ? "You're in." : "You're on the list."}</h2>
              <p className="font-mono text-xs text-zinc-400 mt-3 leading-relaxed">{done.message}</p>
              {done.token && <a data-testid="testing-portal-link" href={`/testing/portal?token=${done.token}`} className="inline-block mt-6 bg-nc-orange hover:bg-nc-orange/80 text-white font-sans font-semibold uppercase tracking-[0.2em] text-xs px-8 py-4 transition-colors">Open your tester portal</a>}
            </div>
          ) : (
            <>
              <p className="font-mono text-[10px] tracking-[0.4em] text-nc-orange uppercase">Apply // 2 minutes</p>
              <h2 className="font-sans font-extrabold uppercase tracking-tight text-3xl text-white mt-3">Get your seat on {program.server_name}</h2>
              <p className="font-mono text-xs text-zinc-500 mt-3 leading-relaxed">Spots open in waves so the server fills at the rate we want to measure. Early applicants go first. Known creators, modders and hub members are approved instantly.</p>
              <div className="mt-8"><TesterApplyForm program={program} onDone={setDone} /></div>
            </>
          )}
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}
